/**
 * Player model builder and procedural animation.
 *
 * One body for every team (Field Tech v2), with the team palette plus a
 * helmet style and a visor style as the per-team variation. The launcher is a
 * shoulder-rested tube; a two-bone IK keeps the right hand on its grip at
 * every aim elevation and in every animation state, and rests the left hand
 * on the hip pouch when standing.
 *
 * Rig: `group` (facing + bob) → `torsoGroup` (lean/twist) → arms, launcher
 * (`aimGroup`, pitches with elevation), head, pack; legs hang off `group`.
 * After `bakePlayerModel()` each moving group's static parts are one mesh per
 * material kind, so a player costs ~11 draw calls regardless of part count.
 *
 * Design reference: design_sheets/player_model_v2.html
 */

import * as THREE from 'three';
import type { HelmetStyle, VisorStyle } from '../types';
import { bakeStaticParts } from './meshBake';

// ============================================================
// TYPES
// ============================================================

export interface TeamColors {
  primary: string;
  accent: string;
  visor: string;
  detail: string;
  helmet?: HelmetStyle;      // default 'dome'
  visorStyle?: VisorStyle;   // default 'band'
}

/** A two-bone arm: shoulder pivot group, elbow pivot group, segment lengths. */
export interface IkArm {
  group: THREE.Group;   // at the shoulder; upper arm hangs along local -Y
  fore: THREE.Group;    // at the elbow; forearm hangs along local -Y, palm at (0, -b, 0)
  a: number;            // shoulder → elbow
  b: number;            // elbow → palm
}

export interface PlayerModel {
  /** Root group — facing (rotation.y), bob and lean applied here */
  group: THREE.Group;

  /** Animation groups */
  torsoGroup: THREE.Group;
  aimGroup: THREE.Group;      // launcher — rotate X for elevation
  armLGroup: THREE.Group;     // free arm shoulder (same object as armL.group)
  legLGroup: THREE.Group;     // left leg — pivot at hip
  legRGroup: THREE.Group;     // right leg — pivot at hip

  /** IK arms and their targets (torsoGroup space unless noted) */
  armL: IkArm;
  armR: IkArm;
  gripR: THREE.Object3D;      // on the held tool (aimGroup space); swapped by the tool system
  /** Held tool: launcher (default) or the build hammer. Both live under aimGroup; one is visible. */
  launcherGroup: THREE.Group;
  hammerGroup: THREE.Group;
  launcherGrip: THREE.Object3D;
  hammerGrip: THREE.Object3D;
  hammerShaft: THREE.Group;   // the hammer's own frame (+Y up the shaft); variants rebuild its children
  heldTool: HeldTool;
  restL: THREE.Object3D;      // hip pouch — left hand rests here when standing
  slingFront: THREE.Object3D; // launcher sling anchor (aimGroup space)
  slingBelt: THREE.Object3D;  // hip sling anchor
  sling: THREE.Mesh;          // thin box re-fit between the anchors every frame

  /** Backpack flag */
  flagMesh: THREE.Mesh;       // flag fabric — apply team texture via setFlagTexture

  /** Reference values */
  legHipY: number;            // default hip pivot Y position
}

export type AnimState = 'idle' | 'waddle' | 'jump' | 'airborne_jump' | 'airborne_knockback' | 'landing' | 'landing_knockback' | 'recoil' | 'build';
export type HeldTool = 'launcher' | 'hammer';

export interface AnimContext {
  state: AnimState;
  time: number;
  jumpStart: number;
  landingStart: number;
  recoilStart: number;
  buildStart: number;
  /** Target aim elevation in radians (0 = horizontal, positive = up) */
  aimElevation: number;
  /** Whether aim is player-controlled (true) or idle drift (false) */
  aimTracking: boolean;
  /** Velocity magnitude for flight animation intensity */
  airborneSpeed: number;
}

// ============================================================
// PRIMITIVES
// ============================================================

const lam = (c: string) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c) });
const glow = (c: string, i = 0.3) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c), emissive: new THREE.Color(c), emissiveIntensity: i });
const capsule = (r: number, len: number, m: THREE.Material, radial = 10) => new THREE.Mesh(new THREE.CapsuleGeometry(r, len, 4, radial), m);
const box = (w: number, h: number, d: number, m: THREE.Material) => new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
const cyl = (rt: number, rb: number, h: number, m: THREE.Material, seg = 12) => new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), m);
const sphere = (r: number, m: THREE.Material, ws = 14, hs = 10) => new THREE.Mesh(new THREE.SphereGeometry(r, ws, hs), m);
const torus = (r: number, tube: number, m: THREE.Material, rs = 8, ts = 24) => new THREE.Mesh(new THREE.TorusGeometry(r, tube, rs, ts), m);
function at<T extends THREE.Object3D>(obj: T, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): T {
  obj.position.set(x, y, z);
  obj.rotation.set(rx, ry, rz);
  return obj;
}
function glass(color: string): THREE.MeshLambertMaterial {
  const m = glow(color, 0.3);
  m.transparent = true;
  m.opacity = 0.85;
  m.side = THREE.DoubleSide;
  return m;
}

const HEAD_Y = 0.88;   // helmet center

// ============================================================
// HELMETS — the lightweight per-team variation, sitting on the helmet sphere
// ============================================================

function buildHelmet(style: HelmetStyle, colors: TeamColors): THREE.Group {
  const g = new THREE.Group();
  const hy = HEAD_Y;
  const seam = () => g.add(at(cyl(0.245, 0.245, 0.03, lam(colors.detail), 16), 0, hy + 0.09, 0));
  switch (style) {
    case 'crest':
      seam();
      g.add(at(box(0.04, 0.05, 0.3, lam(colors.detail)), 0, hy + 0.235, -0.02));
      g.add(at(box(0.024, 0.024, 0.02, glow(colors.visor, 0.6)), 0, hy + 0.235, 0.135));
      break;
    case 'pods':
      seam();
      for (const sx of [-1, 1]) {
        g.add(at(cyl(0.065, 0.065, 0.045, lam(colors.detail), 12), sx * 0.245, hy, 0, 0, 0, Math.PI / 2));
        g.add(at(sphere(0.012, glow(colors.visor, 0.6), 6, 6), sx * 0.27, hy, 0.045));
      }
      g.add(at(cyl(0.012, 0.012, 0.09, lam(colors.accent), 6), 0.19, hy + 0.17, -0.07));
      g.add(at(sphere(0.016, glow(colors.visor), 6, 6), 0.19, hy + 0.22, -0.07));
      break;
    case 'lampbar':
      seam();
      g.add(at(box(0.2, 0.035, 0.03, lam(colors.detail)), 0, hy + 0.165, 0.175, -0.35));
      for (const sx of [-1, 0, 1]) g.add(at(sphere(0.016, glow(colors.visor, 0.8), 8, 6), sx * 0.06, hy + 0.17, 0.195));
      break;
    case 'flaps':
      seam();
      for (const sx of [-1, 1]) g.add(at(box(0.04, 0.16, 0.14, lam(colors.detail)), sx * 0.235, hy - 0.04, 0.02));
      g.add(at(box(0.3, 0.04, 0.03, lam(colors.detail)), 0, hy + 0.12, 0.2, -0.3));
      break;
    case 'hardhat':
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.255, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), lam(colors.detail)), 0, hy + 0.02, 0));
      g.add(at(cyl(0.33, 0.33, 0.02, lam(colors.detail), 20), 0, hy + 0.02, 0));
      g.add(at(box(0.05, 0.04, 0.26, lam(colors.accent)), 0, hy + 0.27, 0));
      break;
    case 'headset':
      for (const sx of [-1, 1]) g.add(at(cyl(0.07, 0.07, 0.05, lam(colors.detail), 12), sx * 0.25, hy, 0, 0, 0, Math.PI / 2));
      g.add(at(new THREE.Mesh(new THREE.TorusGeometry(0.25, 0.014, 6, 24, Math.PI), lam(colors.accent)), 0, hy, 0)); // band over the top
      g.add(at(cyl(0.008, 0.008, 0.2, lam(colors.accent), 4), 0.2, hy - 0.08, 0.12, 0.6, 0, 0.5));          // mic boom
      g.add(at(sphere(0.02, glow(colors.visor, 0.5), 6, 6), 0.14, hy - 0.14, 0.2));
      break;
    case 'cap':
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.25, 16, 8, 0, Math.PI * 2, 0, Math.PI * 0.45), lam(colors.detail)), 0, hy + 0.03, 0));
      g.add(at(box(0.22, 0.015, 0.16, lam(colors.detail)), 0, hy + 0.1, 0.27, -0.15));   // bill
      g.add(at(sphere(0.02, lam(colors.accent), 6, 6), 0, hy + 0.28, 0));                  // button
      g.add(at(box(0.07, 0.04, 0.012, lam(colors.accent)), 0, hy + 0.14, 0.24));          // logo patch
      break;
    case 'dome':
    default:
      seam();
      g.add(at(sphere(0.03, glow(colors.visor, 0.6), 8, 6), 0.12, hy + 0.14, 0.17));      // headlamp
      break;
  }
  return g;
}

// ============================================================
// VISORS
// ============================================================

function buildVisor(style: VisorStyle, colors: TeamColors): THREE.Group {
  const g = new THREE.Group();
  const hy = HEAD_Y;
  switch (style) {
    case 'bubble':
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.25, 20, 12, 0, Math.PI, Math.PI * 0.18, Math.PI * 0.55), glass(colors.visor)), 0, hy, 0));
      break;
    case 'goggles':
      for (const sx of [-1, 1]) {
        g.add(at(torus(0.06, 0.016, lam(colors.detail), 6, 16), sx * 0.09, hy + 0.02, 0.215));
        g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.055, 16), glass(colors.visor)), sx * 0.09, hy + 0.02, 0.222));
      }
      g.add(at(torus(0.245, 0.012, lam(colors.detail), 6, 24), 0, hy + 0.02, 0, Math.PI / 2));   // strap
      break;
    case 'slit':
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.245, 20, 6, Math.PI * 0.12, Math.PI * 0.76, Math.PI * 0.4, Math.PI * 0.12), glass(colors.visor)), 0, hy, 0));
      break;
    case 'band':
    default:
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.245, 20, 8, Math.PI * 0.15, Math.PI * 0.7, Math.PI * 0.3, Math.PI * 0.28), glass(colors.visor)), 0, hy, 0));
      break;
  }
  return g;
}

// ============================================================
// ARMS + LAUNCHER
// ============================================================

const ARM_UPPER = 0.17;   // shoulder → elbow
const ARM_FORE = 0.16;    // elbow → palm

function buildArm(colors: TeamColors): IkArm {
  const group = new THREE.Group();
  group.add(at(capsule(0.06, ARM_UPPER - 0.06, lam(colors.primary)), 0, -ARM_UPPER / 2, 0));
  group.add(at(sphere(0.062, lam(colors.primary), 10, 8), 0, 0, 0));                         // shoulder ball hides the joint
  const fore = new THREE.Group();
  fore.position.set(0, -ARM_UPPER, 0);
  group.add(fore);
  fore.add(at(sphere(0.055, lam(colors.accent), 10, 8), 0, 0, 0));                          // elbow cap
  fore.add(at(capsule(0.052, ARM_FORE - 0.08, lam(colors.primary)), 0, -ARM_FORE / 2 + 0.01, 0));
  fore.add(at(torus(0.056, 0.014, lam(colors.accent), 6, 12), 0, -ARM_FORE + 0.06, 0, Math.PI / 2)); // cuff
  fore.add(at(sphere(0.065, lam(colors.accent), 10, 8), 0, -ARM_FORE, 0));                  // glove
  return { group, fore, a: ARM_UPPER, b: ARM_FORE };
}

/** Launcher in aimGroup space: +Z = fire direction, origin = right-shoulder pivot. The tube rests on the shoulder pad. */
function buildLauncher(colors: TeamColors): { group: THREE.Group; gripR: THREE.Object3D; slingFront: THREE.Object3D } {
  const g = new THREE.Group();
  const tubeY = 0.11;
  g.add(at(cyl(0.052, 0.052, 0.78, lam(colors.detail), 14), 0, tubeY, 0.08, Math.PI / 2));               // tube
  g.add(at(cyl(0.078, 0.052, 0.1, lam(colors.detail), 14), 0, tubeY, 0.52, Math.PI / 2));                 // muzzle flare
  g.add(at(cyl(0.05, 0.072, 0.1, lam(colors.accent), 14), 0, tubeY, -0.36, Math.PI / 2));                 // rear venturi
  g.add(at(cyl(0.06, 0.06, 0.03, lam(colors.accent), 14), 0, tubeY, 0.28, Math.PI / 2));                 // front band
  g.add(at(cyl(0.06, 0.06, 0.04, lam(colors.accent), 14), 0, tubeY, -0.18, Math.PI / 2));                // rear band
  g.add(at(box(0.11, 0.03, 0.16, lam(colors.accent)), 0, 0.05, -0.03));                                    // shoulder saddle
  g.add(at(box(0.02, 0.015, 0.34, lam(colors.accent)), 0, tubeY + 0.058, 0.08));                           // top rail
  g.add(at(box(0.045, 0.045, 0.11, lam(colors.accent)), 0, tubeY + 0.09, 0.0));                            // optic
  g.add(at(sphere(0.016, glow(colors.visor, 0.7), 8, 6), 0, tubeY + 0.09, 0.058));                        // lenses
  g.add(at(sphere(0.016, glow(colors.visor, 0.7), 8, 6), 0, tubeY + 0.09, -0.058));
  g.add(at(box(0.035, 0.1, 0.045, lam(colors.accent)), 0, tubeY - 0.1, 0.22, 0.35));                      // pistol grip, a forearm ahead of the shoulder
  g.add(at(torus(0.035, 0.008, lam(colors.accent), 6, 12), 0, tubeY - 0.07, 0.275, Math.PI / 2));         // trigger guard
  g.add(at(cyl(0.04, 0.04, 0.12, lam(colors.accent), 10), 0, tubeY - 0.06, -0.02, Math.PI / 2));          // ammo drum
  const gripR = at(new THREE.Object3D(), 0, tubeY - 0.13, 0.19);        // palm center on the grip
  const slingFront = at(new THREE.Object3D(), 0, tubeY - 0.05, 0.3);    // sling anchor, tube underside
  g.add(gripR, slingFront);
  return { group: g, gripR, slingFront };
}

/**
 * Build hammer, carried the way a sledge is carried: hand out in front on the lower
 * shaft, head up over the shoulder. Built in aimGroup space (+Z forward, origin =
 * right-shoulder pivot). `shaft` is the hammer's own frame: local +Y runs from the
 * pommel (y≈-0.06) up the shaft to the head (y≈0.46); variants rebuild its children.
 * Design reference: design_sheets/build_hammer.html
 */
export const HAMMER_GRIP_POS = new THREE.Vector3(0.04, 0.02, 0.22);     // hand position (aimGroup space)
export const HAMMER_SHAFT_DIR = new THREE.Vector3(0, 0.75, -0.66).normalize(); // grip → head: up and back over the shoulder
export const HAMMER_HEAD_Y = 0.46;                                       // head center along the shaft frame

export function buildHammer(colors: TeamColors): { group: THREE.Group; shaft: THREE.Group; grip: THREE.Object3D } {
  const g = new THREE.Group();
  const shaft = new THREE.Group();
  shaft.position.copy(HAMMER_GRIP_POS);
  shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), HAMMER_SHAFT_DIR);
  g.add(shaft);
  shaft.add(...buildSledgeParts(colors));
  const grip = at(new THREE.Object3D(), 0, 0.05, 0);   // palm a little up from the pommel
  shaft.add(grip);
  return { group: g, shaft, grip };
}

/** Shipped head: square sledge across the shaft, team striking faces, tally light. Shaft-frame parts. */
export function buildSledgeParts(colors: TeamColors): THREE.Object3D[] {
  const headY = HAMMER_HEAD_Y;
  return [
    at(cyl(0.02, 0.022, 0.52, lam(colors.detail), 10), 0, 0.2, 0),                 // shaft (y -0.06 .. 0.46)
    at(cyl(0.028, 0.028, 0.14, lam(colors.accent), 10), 0, 0.03, 0),               // grip wrap
    at(cyl(0.026, 0.026, 0.02, lam(colors.accent), 10), 0, -0.06, 0),              // pommel
    at(box(0.09, 0.09, 0.22, lam(colors.accent)), 0, headY, 0),                     // head, across the shaft (long axis = swing plane)
    at(cyl(0.05, 0.05, 0.03, lam(colors.detail), 12), 0, headY, 0.115, Math.PI / 2),   // striking faces
    at(cyl(0.05, 0.05, 0.03, lam(colors.detail), 12), 0, headY, -0.115, Math.PI / 2),
    at(box(0.1, 0.1, 0.03, lam(colors.detail)), 0, headY, 0),                       // center band
    at(box(0.012, 0.02, 0.02, glow(colors.visor, 0.6)), 0.05, headY + 0.03, 0),     // tally light
  ];
}

/** Hammer rest pose while in build mode: head up over the shoulder (aimGroup X rotation). */
export const HAMMER_REST = -0.1;
/** Seconds the build swing plays (two strikes). */
export const BUILD_SWING_DURATION = 0.9;

// ============================================================
// MODEL BUILDER
// ============================================================

export function buildPlayerModel(colors: TeamColors): PlayerModel {
  const group = new THREE.Group();
  const hipY = 0.29;

  // --- legs (pivot at hip) ---
  const leg = (sx: number): THREE.Group => {
    const g = new THREE.Group();
    g.position.set(sx * 0.11, hipY, 0);
    g.add(at(sphere(0.08, lam(colors.accent), 10, 8), 0, 0, 0));                         // hip ball
    g.add(at(capsule(0.075, 0.12, lam(colors.accent)), 0, -0.11, 0));
    g.add(at(sphere(0.052, lam(colors.detail), 10, 8), 0, -0.14, 0.065));                // knee pad
    g.add(at(cyl(0.08, 0.085, 0.07, lam(colors.detail), 12), 0, -0.21, 0.01));          // boot cuff
    g.add(at(box(0.15, 0.085, 0.2, lam(colors.detail)), 0, -0.255, 0.03));               // boot
    g.add(at(box(0.15, 0.03, 0.05, lam(colors.accent)), 0, -0.235, 0.14));              // toe cap
    return g;
  };
  const legLGroup = leg(-1);
  const legRGroup = leg(1);
  group.add(legLGroup, legRGroup);

  // --- torso ---
  const torsoGroup = new THREE.Group();
  const body = at(capsule(0.235, 0.2, lam(colors.primary), 16), 0, 0.52, 0);
  body.scale.z = 0.9;
  torsoGroup.add(body);
  torsoGroup.add(at(box(0.17, 0.13, 0.04, lam(colors.detail)), 0, 0.58, 0.215));         // chest panel
  torsoGroup.add(at(box(0.03, 0.03, 0.012, glow(colors.visor, 0.6)), 0.05, 0.61, 0.238)); // status light
  torsoGroup.add(at(box(0.08, 0.012, 0.012, lam(colors.accent)), -0.03, 0.55, 0.238));   // panel slot
  torsoGroup.add(at(cyl(0.24, 0.24, 0.045, lam(colors.detail), 16), 0, 0.34, 0));        // belt
  torsoGroup.add(at(box(0.06, 0.04, 0.02, lam(colors.accent)), 0, 0.34, 0.24));           // buckle
  torsoGroup.add(at(box(0.07, 0.08, 0.05, lam(colors.accent)), -0.19, 0.33, 0.1, 0, 0.5, 0)); // hip pouch (left)
  for (const sx of [-1, 1]) {
    const pad = at(sphere(0.09, lam(colors.accent), 12, 8), sx * 0.25, 0.70, 0);
    pad.scale.y = 0.7;
    torsoGroup.add(pad);
  }
  torsoGroup.add(at(cyl(0.13, 0.15, 0.06, lam(colors.detail), 12), 0, 0.705, 0));        // neck ring
  const head = at(sphere(0.24, lam(colors.accent), 18, 14), 0, HEAD_Y, 0);
  head.scale.y = 0.92;
  torsoGroup.add(head);
  torsoGroup.add(buildVisor(colors.visorStyle ?? 'band', colors));
  torsoGroup.add(buildHelmet(colors.helmet ?? 'dome', colors));

  // backpack
  torsoGroup.add(at(box(0.3, 0.46, 0.16, lam(colors.detail)), 0, 0.58, -0.25));
  torsoGroup.add(at(box(0.22, 0.1, 0.06, lam(colors.accent)), 0, 0.37, -0.31));         // lower pouch
  for (const sx of [-1, 1]) {
    torsoGroup.add(at(cyl(0.045, 0.045, 0.38, lam(colors.accent), 10), sx * 0.09, 0.58, -0.35));
    torsoGroup.add(at(sphere(0.045, lam(colors.accent), 8, 6), sx * 0.09, 0.78, -0.35));
    torsoGroup.add(at(cyl(0.015, 0.015, 0.03, lam(colors.detail), 6), sx * 0.09, 0.835, -0.35));  // valve
  }

  // flag — the fabric gets the team texture and flutters, so it stays its own mesh
  torsoGroup.add(at(cyl(0.01, 0.01, 0.3, lam('#a0a4b0'), 4), 0.1, 0.98, -0.25));
  const flagMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.14, 0.1),
    new THREE.MeshLambertMaterial({ side: THREE.DoubleSide, color: new THREE.Color(colors.accent) }),
  );
  flagMesh.position.set(0.17, 1.08, -0.25);
  flagMesh.userData.noBake = true;
  torsoGroup.add(flagMesh);

  // antenna
  torsoGroup.add(at(cyl(0.008, 0.008, 0.14, lam(colors.detail), 4), -0.13, 1.0, -0.1, 0, 0, 0.15));
  torsoGroup.add(at(sphere(0.02, glow(colors.visor), 6, 6), -0.14, 1.07, -0.1));

  // --- arms (IK) ---
  const armL = buildArm(colors);
  armL.group.position.set(-0.27, 0.70, 0);
  const armR = buildArm(colors);
  armR.group.position.set(0.27, 0.70, 0);
  torsoGroup.add(armL.group, armR.group);

  // --- launcher on the right shoulder; the whole aimGroup pitches with elevation ---
  const aimGroup = new THREE.Group();
  aimGroup.position.set(0.31, 0.70, 0.02);
  const launcher = buildLauncher(colors);
  const hammer = buildHammer(colors);
  hammer.group.visible = false;
  aimGroup.add(launcher.group, hammer.group);
  torsoGroup.add(aimGroup);

  // sling: tube front → right hip, side/back (re-fit every frame, so never baked)
  const sling = box(0.016, 1, 0.006, lam(colors.accent));
  sling.scale.y = 0.01;
  sling.userData.noBake = true;
  torsoGroup.add(sling);
  const slingBelt = at(new THREE.Object3D(), 0.27, 0.42, -0.1);
  const restL = at(new THREE.Object3D(), -0.2, 0.46, 0.1);    // hand on the hip pouch, elbow bent out
  torsoGroup.add(slingBelt, restL);

  group.add(torsoGroup);

  return {
    group, torsoGroup, aimGroup, armLGroup: armL.group, legLGroup, legRGroup,
    armL, armR, gripR: launcher.gripR, restL, slingFront: launcher.slingFront, slingBelt, sling,
    launcherGroup: launcher.group, hammerGroup: hammer.group, launcherGrip: launcher.gripR, hammerGrip: hammer.grip, hammerShaft: hammer.shaft, heldTool: 'launcher',
    flagMesh, legHipY: hipY,
  };
}

/**
 * Merge each moving group's static parts into one mesh per material kind
 * (opaque / emissive / glass). Call once after the flag texture is set.
 * Returns the number of parts folded away.
 */
export function bakePlayerModel(model: PlayerModel): number {
  const moving: THREE.Object3D[] = [
    model.torsoGroup, model.legLGroup, model.legRGroup,
    model.armL.group, model.armL.fore, model.armR.group, model.armR.fore,
    model.aimGroup, model.launcherGroup, model.hammerGroup,   // tools bake separately so they can be toggled
  ];
  const isUnder = (o: THREE.Object3D, root: THREE.Object3D): boolean => {
    for (let p = o.parent; p; p = p.parent) if (p === root) return true;
    return false;
  };
  let baked = 0;
  for (const g of moving) {
    // Keep only the moving groups nested inside this one separate (an ancestor in the
    // exclude list would exclude this whole subtree, since exclusion is by descendant)
    baked += bakeStaticParts(g, { exclude: moving.filter(o => o !== g && isUnder(o, g)) }).partsBaked;
  }
  return baked;
}

// ============================================================
// FLAG TEXTURE
// ============================================================

export function setFlagTexture(model: PlayerModel, texture: THREE.Texture): void {
  const flagMat = model.flagMesh.material as THREE.MeshLambertMaterial;
  flagMat.map = texture;
  flagMat.needsUpdate = true;
}

// ============================================================
// HELD TOOL
// ============================================================

/** Choose what the player carries. Takes effect on the next animation frame; the build swing always shows the hammer. */
export function setHeldTool(model: PlayerModel, tool: HeldTool): void {
  model.heldTool = tool;
}

function applyTool(model: PlayerModel, ctx: AnimContext): HeldTool {
  const tool: HeldTool = ctx.state === 'build' ? 'hammer' : model.heldTool;
  const hammer = tool === 'hammer';
  model.hammerGroup.visible = hammer;
  model.launcherGroup.visible = !hammer;
  model.sling.visible = !hammer;
  model.gripR = hammer ? model.hammerGrip : model.launcherGrip;
  return tool;
}

// ============================================================
// IK
// ============================================================

const _v = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _e = new THREE.Vector3();
const _u = new THREE.Vector3();
const _f = new THREE.Vector3();
const _fl = new THREE.Vector3();
const _y = new THREE.Vector3();
const _p = new THREE.Vector3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _qi = new THREE.Quaternion();
const _qr = new THREE.Quaternion();

const POLE_R = new THREE.Vector3(1, -0.6, -0.2);    // right elbow: out, down, a little back
const POLE_L = new THREE.Vector3(-1, -0.4, -0.5);   // left elbow: out and back

/**
 * Two-bone IK with an elbow hint: orient `arm` (in its parent's space) so the
 * palm lands on `target`, with the elbow pushed toward `pole` so it never
 * folds into the body. Unreachable targets clamp to full extension.
 */
export function solveArm(arm: IkArm, target: THREE.Vector3, pole: THREE.Vector3): void {
  const S = arm.group.position;
  _v.copy(target).sub(S);
  const d = Math.min(arm.a + arm.b - 1e-3, Math.max(Math.abs(arm.a - arm.b) + 1e-3, _v.length()));
  _axis.copy(_v).normalize();

  // Elbow: on the circle around the shoulder→target axis, on the pole's side
  const proj = (arm.a * arm.a - arm.b * arm.b + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(0, arm.a * arm.a - proj * proj));
  _perp.copy(pole).addScaledVector(_axis, -pole.dot(_axis));
  if (_perp.lengthSq() < 1e-6) _perp.set(0, 0, -1).addScaledVector(_axis, _axis.z);
  _perp.normalize();
  _e.copy(_axis).multiplyScalar(proj).addScaledVector(_perp, h);        // elbow, relative to shoulder

  // Upper arm: local -Y → elbow direction, then roll so the forearm's bend plane (local +Z) faces the hand
  _u.copy(_e).normalize();
  _q.setFromUnitVectors(_y.set(0, -1, 0), _u);
  _f.copy(_axis).multiplyScalar(d).sub(_e).normalize();                 // forearm direction (elbow → hand)
  _fl.copy(_f).applyQuaternion(_qi.copy(_q).invert());
  _q.multiply(_qr.setFromAxisAngle(_y.set(0, 1, 0), Math.atan2(_fl.x, _fl.z)));
  arm.group.quaternion.copy(_q);

  const elbow = Math.acos(Math.max(-1, Math.min(1, (arm.a * arm.a + arm.b * arm.b - d * d) / (2 * arm.a * arm.b))));
  arm.fore.rotation.set(-(Math.PI - elbow), 0, 0);
}

/** Position of any descendant of aimGroup (grip targets, sling anchors), expressed in torsoGroup space. */
function inTorso(model: PlayerModel, obj: THREE.Object3D, out: THREE.Vector3): THREE.Vector3 {
  out.set(0, 0, 0);
  for (let o: THREE.Object3D | null = obj; o; o = o.parent) {
    o.updateMatrix();
    out.applyMatrix4(o.matrix);
    if (o === model.aimGroup) break;
  }
  return out;
}

function updateIK(model: PlayerModel, ctx: AnimContext): void {
  // Right hand: always on the grip, so the launcher is held at any elevation (and while tumbling)
  solveArm(model.armR, inTorso(model, model.gripR, _p), POLE_R);
  // Left hand: on the hip pouch when standing still; the state animations own it otherwise
  if (ctx.state === 'idle' || ctx.state === 'recoil') solveArm(model.armL, model.restL.position, POLE_L);
  else model.armL.fore.rotation.set(-0.5, 0, 0);
  // Sling: thin box stretched from the tube front to the hip anchor
  inTorso(model, model.slingFront, _a);
  _b.copy(model.slingBelt.position);
  const len = _a.distanceTo(_b);
  model.sling.position.copy(_a).add(_b).multiplyScalar(0.5);
  model.sling.scale.y = len;
  model.sling.quaternion.setFromUnitVectors(_v.set(0, 1, 0), _b.sub(_a).normalize());
}

// ============================================================
// ANIMATION
// ============================================================

export function createAnimContext(): AnimContext {
  return {
    state: 'idle',
    time: 0,
    jumpStart: 0,
    landingStart: 0,
    recoilStart: 0,
    buildStart: 0,
    aimElevation: 0,
    aimTracking: false,
    airborneSpeed: 0,
  };
}

/** Reset an existing AnimContext to idle (e.g. after respawn). */
export function resetAnimContext(ctx: AnimContext): void {
  ctx.state = 'idle';
  ctx.time = 0;
  ctx.jumpStart = 0;
  ctx.landingStart = 0;
  ctx.recoilStart = 0;
  ctx.buildStart = 0;
  ctx.aimElevation = 0;
  ctx.aimTracking = false;
  ctx.airborneSpeed = 0;
}

/**
 * Call once per frame. Mutates the PlayerModel groups in place.
 */
export function updateAnimation(model: PlayerModel, ctx: AnimContext, dt: number): void {
  ctx.time += dt;
  const t = ctx.time;

  // --- BACKPACK FLAG FLUTTER ---
  model.flagMesh.rotation.y = Math.sin(t * 3) * 0.1;

  // --- AIM ELEVATION (negated: model's +X rotation tilts down, so negate for positive=up) ---
  if (ctx.aimTracking) {
    model.aimGroup.rotation.x = -ctx.aimElevation;
  } else {
    // Idle drift: gentle sweep
    model.aimGroup.rotation.x = -(Math.sin(t * 0.8) * 0.15 + 0.15);
  }

  // --- FREE ARM: gentle counter-swing (overridden by IK when standing) ---
  model.armLGroup.rotation.x = Math.sin(t * 1.5) * 0.08;

  // --- STATE-SPECIFIC ANIMATION ---
  switch (ctx.state) {
    case 'idle': {
      model.group.position.y = Math.sin(t * 2) * 0.015;
      model.group.rotation.z = 0;
      model.legLGroup.position.y = model.legHipY;
      model.legRGroup.position.y = model.legHipY;
      model.legLGroup.rotation.x = 0;
      model.legRGroup.rotation.x = 0;
      model.torsoGroup.rotation.y = 0;
      break;
    }

    case 'waddle': {
      const wt = t * 4;
      model.group.position.y = Math.sin(wt) * 0.02;
      model.group.rotation.z = Math.sin(wt) * 0.14;               // side lean
      const hipTwist = Math.sin(wt) * 0.18;                        // Y twist
      model.legLGroup.rotation.x = Math.sin(wt) * 0.15;           // tiny kicks
      model.legRGroup.rotation.x = Math.sin(wt + Math.PI) * 0.15;
      model.legLGroup.position.y = model.legHipY;
      model.legRGroup.position.y = model.legHipY;
      model.torsoGroup.rotation.y = hipTwist;
      model.armLGroup.rotation.set(Math.sin(wt) * 0.35, 0, -0.2); // free arm swings with the walk
      break;
    }

    case 'jump': {
      if (ctx.jumpStart === 0) ctx.jumpStart = t;
      const elapsed = t - ctx.jumpStart;
      const dur = 0.8;
      const p = Math.min(elapsed / dur, 1);

      if (p < 0.2) {
        // Crouch — legs compress into body
        const crouch = p / 0.2;
        model.legLGroup.position.y = model.legHipY + crouch * 0.15;
        model.legRGroup.position.y = model.legHipY + crouch * 0.15;
        model.group.position.y = -crouch * 0.05;
      } else if (p < 0.6) {
        // Airborne — legs extend, body arcs up
        const air = (p - 0.2) / 0.4;
        const arc = Math.sin(air * Math.PI);
        model.legLGroup.position.y = model.legHipY + 0.15 - arc * 0.2;
        model.legRGroup.position.y = model.legHipY + 0.15 - arc * 0.2;
        model.group.position.y = -0.05 + arc * 0.35;
      } else {
        // Landing settle
        const land = (p - 0.6) / 0.4;
        const settle = 1 - land;
        model.legLGroup.position.y = model.legHipY - settle * 0.05;
        model.legRGroup.position.y = model.legHipY - settle * 0.05;
        model.group.position.y = settle * 0.02;
      }

      if (p >= 1) {
        ctx.jumpStart = 0;
        ctx.state = 'idle';
      }

      model.legLGroup.rotation.x = 0;
      model.legRGroup.rotation.x = 0;
      model.group.rotation.z = 0;
      model.torsoGroup.rotation.y = 0;
      break;
    }

    case 'airborne_jump': {
      const speedFactor = Math.max(0.2, Math.min(1.5, ctx.airborneSpeed / 15));

      // Free arm spread out with wave; launcher drifts
      model.armLGroup.rotation.set(
        Math.sin(t * 3 * speedFactor) * 0.15 * speedFactor,
        0,
        -1.2 + Math.sin(t * 4 * speedFactor) * 0.2 * speedFactor,
      );
      model.aimGroup.rotation.x = -0.3 + Math.sin(t * 3.5 * speedFactor) * 0.1 * speedFactor;

      // Legs together, slightly bent, gentle sway
      model.legLGroup.rotation.x = 0.15 + Math.sin(t * 3 * speedFactor) * 0.1 * speedFactor;
      model.legRGroup.rotation.x = 0.15 + Math.sin(t * 3 * speedFactor + Math.PI) * 0.1 * speedFactor;
      model.legLGroup.position.y = model.legHipY;
      model.legRGroup.position.y = model.legHipY;

      // Body tilt forward
      model.torsoGroup.rotation.x = 0.15;
      model.torsoGroup.rotation.y = 0;

      // Subtle breathing bob
      model.group.position.y = Math.sin(t * 3) * 0.01;
      model.group.rotation.z = 0;
      break;
    }

    case 'airborne_knockback': {
      const speedFactor = Math.max(0.2, Math.min(1.5, ctx.airborneSpeed / 15));

      // Rapid torso tumble
      model.torsoGroup.rotation.z = t * 6 * speedFactor;
      model.torsoGroup.rotation.y = Math.sin(t * 4) * 0.3;
      model.torsoGroup.rotation.x = 0;

      // Legs splay apart
      model.legLGroup.rotation.x = Math.sin(t * 5 * speedFactor) * 0.6;
      model.legRGroup.rotation.x = Math.sin(t * 5 * speedFactor + Math.PI) * 0.6;
      model.legLGroup.position.y = model.legHipY;
      model.legRGroup.position.y = model.legHipY;

      // Free arm flails; launcher swings on its sling
      model.armLGroup.rotation.set(Math.sin(t * 7 * speedFactor) * 0.8, 0, -0.15 + Math.sin(t * 5) * 0.4);
      model.aimGroup.rotation.x = Math.sin(t * 7 * speedFactor + 1) * 0.5;

      model.group.position.y = 0;
      model.group.rotation.z = 0;
      break;
    }

    case 'landing': {
      if (ctx.landingStart === 0) ctx.landingStart = t;
      const elapsed = t - ctx.landingStart;
      const dur = 0.5;
      const p = Math.min(elapsed / dur, 1);

      if (p < 0.4) {
        // Impact crouch
        const crouch = p / 0.4;
        model.legLGroup.position.y = model.legHipY + crouch * 0.1;
        model.legRGroup.position.y = model.legHipY + crouch * 0.1;
        model.group.position.y = -crouch * 0.06;
      } else {
        // Stand up
        const stand = (p - 0.4) / 0.6;
        model.legLGroup.position.y = model.legHipY + (1 - stand) * 0.1;
        model.legRGroup.position.y = model.legHipY + (1 - stand) * 0.1;
        model.group.position.y = -(1 - stand) * 0.06;
      }

      model.legLGroup.rotation.x = 0;
      model.legRGroup.rotation.x = 0;
      model.group.rotation.z = 0;
      model.torsoGroup.rotation.set(0, 0, 0);
      model.armLGroup.rotation.set(0.3, 0, -0.6);               // arms out for balance

      if (p >= 1) {
        ctx.landingStart = 0;
        ctx.state = 'idle';
      }
      break;
    }

    case 'landing_knockback': {
      if (ctx.landingStart === 0) ctx.landingStart = t;
      const elapsed = t - ctx.landingStart;
      const dur = 0.7;
      const p = Math.min(elapsed / dur, 1);

      if (p < 0.3) {
        // Hard impact — crouch with residual tumble wobble
        const impact = p / 0.3;
        model.legLGroup.position.y = model.legHipY + impact * 0.12;
        model.legRGroup.position.y = model.legHipY + impact * 0.12;
        model.group.position.y = -impact * 0.08;
        model.torsoGroup.rotation.z = Math.sin(t * 12) * 0.15 * (1 - impact);
        model.torsoGroup.rotation.x = 0.1 * (1 - impact);
      } else if (p < 0.7) {
        // Recovery — torso rotation unwinds, body straightens
        const recover = (p - 0.3) / 0.4;
        model.legLGroup.position.y = model.legHipY + (1 - recover) * 0.12;
        model.legRGroup.position.y = model.legHipY + (1 - recover) * 0.12;
        model.group.position.y = -(1 - recover) * 0.08;
        model.torsoGroup.rotation.z = (1 - recover) * 0.1;
        model.torsoGroup.rotation.x = 0;
      } else {
        // Stand — smooth return to idle
        const stand = (p - 0.7) / 0.3;
        model.legLGroup.position.y = model.legHipY;
        model.legRGroup.position.y = model.legHipY;
        model.group.position.y = 0;
        model.torsoGroup.rotation.z = (1 - stand) * 0.03;
        model.torsoGroup.rotation.x = 0;
      }

      model.legLGroup.rotation.x = 0;
      model.legRGroup.rotation.x = 0;
      model.group.rotation.z = 0;
      model.torsoGroup.rotation.y = 0;
      model.armLGroup.rotation.set(0.3, 0, -0.6);

      if (p >= 1) {
        ctx.landingStart = 0;
        ctx.state = 'idle';
      }
      break;
    }

    case 'recoil': {
      if (ctx.recoilStart === 0) ctx.recoilStart = t;
      const elapsed = t - ctx.recoilStart;
      const dur = 0.4;
      const p = Math.min(elapsed / dur, 1);

      if (p < 0.25) {
        // Kickback — torso leans back, aim kicks up
        const kick = p / 0.25;
        model.torsoGroup.rotation.x = -0.2 * kick;
        model.aimGroup.rotation.x = -ctx.aimElevation - 0.3 * kick;
        model.group.position.y = -0.02 * kick;
      } else {
        // Return — smooth lerp back to aimed position
        const ret = (p - 0.25) / 0.75;
        model.torsoGroup.rotation.x = -0.2 * (1 - ret);
        model.aimGroup.rotation.x = -ctx.aimElevation - 0.3 * (1 - ret);
        model.group.position.y = -0.02 * (1 - ret);
      }

      model.legLGroup.position.y = model.legHipY;
      model.legRGroup.position.y = model.legHipY;
      model.legLGroup.rotation.x = 0;
      model.legRGroup.rotation.x = 0;
      model.group.rotation.z = 0;
      model.torsoGroup.rotation.y = 0;

      if (p >= 1) {
        ctx.recoilStart = 0;
        ctx.state = 'idle';
      }
      break;
    }

    case 'build': {
      // Two hammer strikes: raise over the shoulder, drive down-forward, recover
      if (ctx.buildStart === 0) ctx.buildStart = t;
      const p = Math.min((t - ctx.buildStart) / BUILD_SWING_DURATION, 1);
      const c = (p * 2) % 1;                                   // per-strike phase
      const ease = (x: number) => x * x * (3 - 2 * x);
      let swing: number;
      let strike = 0;
      const RAISE = -0.7, STRIKE = 1.9;                        // aimGroup X: negative = head back, positive = over the top and down
      if (c < 0.45) swing = HAMMER_REST + (RAISE - HAMMER_REST) * ease(c / 0.45);
      else if (c < 0.7) { strike = ease((c - 0.45) / 0.25); swing = RAISE + (STRIKE - RAISE) * strike; }
      else { const r = ease((c - 0.7) / 0.3); swing = STRIKE + (HAMMER_REST - STRIKE) * r; strike = 1 - r; }
      model.aimGroup.rotation.x = swing;
      model.torsoGroup.rotation.x = 0.45 * strike;              // bend into the blow
      model.torsoGroup.rotation.y = 0;
      model.group.position.y = -0.03 * strike;
      model.group.rotation.z = 0;
      model.legLGroup.position.y = model.legHipY + 0.03 * strike;
      model.legRGroup.position.y = model.legHipY + 0.03 * strike;
      model.legLGroup.rotation.x = 0;
      model.legRGroup.rotation.x = 0;
      if (p >= 1) {
        ctx.buildStart = 0;
        ctx.state = 'idle';
        model.torsoGroup.rotation.x = 0;
      }
      break;
    }
  }

  // --- HELD TOOL: hammer rests over the shoulder instead of tracking aim ---
  const tool = applyTool(model, ctx);
  if (tool === 'hammer' && ctx.state !== 'build' && ctx.state !== 'airborne_knockback' && ctx.state !== 'airborne_jump') {
    model.aimGroup.rotation.x = HAMMER_REST;
  }

  // --- HANDS + SLING follow whatever the state did ---
  updateIK(model, ctx);
}
